import {
  Modal,
  Drawer,
  Text,
  Button,
  Group,
  TextInput,
  Loader,
  Flex,
  Box,
  Table,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetOrderById } from '../../hooks/orders/queries';
import FrequentCustomerCardSummary from '../FrequentCustomerCardSummary';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import { IOrder, IOrderPaymentLines } from '../../interfaces/Order';
import { IStore } from '../../interfaces/Store';
import OrderStatuses from '../../enum/OrderStatuses';
import { useTranslation } from 'react-i18next';
import { IOrderProduct } from '../../interfaces/Product';
import OrderPaymentLinesModal from '../OrderModals/OrderPaymentLinesModal';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import {
  useSetOrderAccepted,
  useSetOrderCancelled,
  useSetOrderCleaned,
  useSetOrderCleaning,
  useSetOrderCollected,
  useSetOrderDelivered,
  useSetOrderDelivering,
  useSetOrderPicking
} from '../../hooks/orders/mutations';
import { showNotification } from '../../utils/Notification/Notification';
import { useHistory } from 'react-router-dom';
import { generateProductsRows } from '../OrderModals/OrderProductsModal';
import { IconQrcode } from '@tabler/icons-react';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from '../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../utils/monitoring/Monitoring_Events_Enum';
import { useGetStoreById } from '../../hooks/stores/queries';
import { getPiecesBreakdown, formatPiecesBreakdown } from '../../utils/helpers/getAllPieces';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { parseScannedCode } from '@washday/utilities';
import { CustomerScannedModalContent } from './CustomerScannedModal';
import { useOrderPaymentLinesModal } from '../../hooks/useOrderPaymentLinesModal';
import useCanEditOrders from '../../hooks/useCanEditOrders';
import { useSelector } from 'react-redux';
import { createWebCompletionTicketAutoPrinter } from '../../utils/orderCompletionTicket';
import { useRequestCashierPinValidation } from '../../hooks/useRequestCashierPinValidation';
import OrderNotificationModal from '../OrderNotificationModal';

const ScannedOrderDetails = ({
  order,
  refetchOrder,
  close,
  openNotifications
}: {
  order: IOrder;
  refetchOrder: () => void;
  close: () => void;
  openNotifications: () => void;
}) => {
  const posthog = usePostHog();
  const company = useSelector((state: any) => state.user.company);

  const { t } = useTranslation([
    'FieldName',
    'ScanOrderModal',
    'OrdersPage',
    'CommonButtons',
    'UpdateOrderForm',
    'OrdersCardMoreActionMenu'
  ]);
  const setOrderCancelled = useSetOrderCancelled();
  const setOrderAccepted = useSetOrderAccepted();
  const setOrderCleaned = useSetOrderCleaned();
  const setOrderCleaning = useSetOrderCleaning();
  const setOrderDelivered = useSetOrderDelivered();
  const setOrderCollected = useSetOrderCollected();
  const setOrderPicking = useSetOrderPicking();
  const setOrderDelivering = useSetOrderDelivering();
  const history = useHistory();
  const { requestPin } = useRequestCashierPinValidation();
  const collectionRequest = useRef<AbortController | null>(null);
  const sourceStoreId = typeof order.createdIn === 'string' ? order.createdIn : order.createdIn?._id || '';
  useEffect(() => () => {
    collectionRequest.current?.abort();
    collectionRequest.current = null;
  }, [order._id, sourceStoreId]);
  const canEditOrders = useCanEditOrders();
  const { data: store } = useGetStoreById(!!sourceStoreId, sourceStoreId);
  const paymentLinesModal = useOrderPaymentLinesModal({ store });
  const completionTicketAutoPrint = useMemo(
    () => createWebCompletionTicketAutoPrinter({
      t,
      companyLogoUrl: company?.logoUrl,
      companyTaxInfo: company?.companyTaxInfo
    }),
    [company?.companyTaxInfo, company?.logoUrl, t]
  );

  const totalPaidAmount = order?.paymentLines?.reduce(
    (acc: number, line: IOrderPaymentLines) => acc + line.amountPaid,
    0
  );

  const amountDue = (order?.total || 0) - totalPaidAmount;

  const orderProducts = order?.products || [];
  const orderBuyAndGerProducts = order?.buyAndGetProducts || [];
  const allPieces = formatPiecesBreakdown(getPiecesBreakdown(orderProducts, orderBuyAndGerProducts), {
    measurementUnits: (order?.createdIn as IStore)?.measurementUnits
  });

  const rows = generateProductsRows(order as IOrder, t);

  const onOrderCancelled = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderCancelled.isPending) return;
      await setOrderCancelled.mutateAsync({
        sequence: sequence,
        storeId: storeId,
        data: {
          cancelledDateTime: new Date().toISOString()
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      refetchOrder();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderAccepted = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderAccepted.isPending) return;
      await setOrderAccepted.mutateAsync({
        sequence: sequence,
        storeId: storeId || ''
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      refetchOrder();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderCleaned = async ({
    sequence,
    storeId,
    params
  }: {
    sequence: string;
    storeId: string;
    params: {
      markOutsourcedAsReturned?: boolean;
    };
  }) => {
    try {
      if (setOrderCleaned.isPending) return;
      await setOrderCleaned.mutateAsync({
        sequence: sequence,
        storeId: storeId || '',
        data: {
          cleanedDateTime: new Date().toISOString(),
          readyDateTime: new Date().toISOString()
        },
        params: params
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      refetchOrder();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderCleaning = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderCleaning.isPending) return;
      await setOrderCleaning.mutateAsync({
        sequence: sequence,
        storeId: storeId || ''
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      refetchOrder();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderDelivered = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    return onOrderCollected({ sequence, storeId, delivered: true, collectWithAmountDue: true });
  };

  const onOrderCollected = async ({ sequence, storeId, collectWithAmountDue = false, delivered = false }: {
    sequence: string; storeId: string; collectWithAmountDue?: boolean; delivered?: boolean;
  }) => {
    if (collectionRequest.current || setOrderCollected.isPending || setOrderDelivered.isPending) return;
    const operation = new AbortController();
    collectionRequest.current = operation;
    try {
      if (!store || store._id !== storeId || sourceStoreId !== storeId) throw new Error('Store unavailable');
      const pinUser = store.requireCashierPinToCollect ? await requestPin({
        purpose: 'order-collection', storeId, signal: operation.signal
      }) : null;
      if (operation.signal.aborted || (store.requireCashierPinToCollect && !pinUser?._id)) return;
      const complete = delivered ? setOrderDelivered : setOrderCollected;
      await complete.mutateAsync({ sequence, storeId,
        data: { orderId: order._id, collectedDateTime: new Date().toISOString(), collectWithAmountDue,
          ...(pinUser?._id ? { pinUserId: pinUser._id } : {}),
          ...(pinUser?.pinValidationToken ? { pinValidationToken: pinUser.pinValidationToken } : {})
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      refetchOrder();
      void completionTicketAutoPrint(order._id, store.orderPageConfig?.printTicketAfterOrderCompletion);
    } catch {
      if (!operation.signal.aborted) {
        showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
        refetchOrder();
      }
    } finally {
      if (collectionRequest.current === operation) collectionRequest.current = null;
    }
  };

  const onOrderPicking = async ({ sequence, storeId }: { sequence: string; storeId: string }) => {
    try {
      if (setOrderPicking.isPending) return;
      await setOrderPicking.mutateAsync({
        sequence: sequence,
        storeId: storeId || '',
        data: {
          pickingUpDateTime: new Date().toISOString()
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      refetchOrder();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onOrderDelivering = async ({
    sequence,
    storeId
  }: {
    sequence: string;
    storeId: string;
  }) => {
    try {
      if (setOrderDelivering.isPending) return;
      await setOrderDelivering.mutateAsync({
        sequence: sequence,
        storeId: storeId || '',
        data: {
          deliveringDateTime: new Date().toISOString()
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      refetchOrder();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    }
  };

  const onEditOrder = () => {
    if (!canEditOrders) return;
    history.push(`/orders/${order._id}?storeId=${order.createdIn?._id}`, {
      storeID: order.createdIn?._id
    });
    close();
  };

  useEffect(() => {
    paymentLinesModal.syncOrder(order);
  }, [order]);

  const showPaymentLinesModal = async (order: IOrder) => {
    await paymentLinesModal.open(order);
  };

  const getOrderNextStatusAndAction = (order: IOrder) => {
    switch (order.status) {
      case OrderStatuses.CANCELLED:
        //we don't need to do anything, order is cancelled
        return {
          label: '',
          action: () => {}
        };
      case OrderStatuses.CREATED:
        return {
          label: t('UpdateOrderForm:updateOrderOptions.markAsAccepted'), //we assume is cleaning but could be PICKUP next status
          action: onOrderAccepted // backend will decide the next status
        };
      case OrderStatuses.PICKUP:
        return {
          label: t('UpdateOrderForm:updateOrderOptions.markAsPickingup'),
          action: onOrderPicking
        };
      case OrderStatuses.PICKINGUP:
        return {
          label: t('UpdateOrderForm:updateOrderOptions.markAsCleaning'),
          action: onOrderCleaning
        };
      case OrderStatuses.CLEANING:
        return {
          label: t('UpdateOrderForm:updateOrderOptions.markAsCleaned'),
          action: onOrderCleaned // backend will decide the next status between READY or DELIVERY
        };
      case OrderStatuses.READY:
        return {
          label: t('UpdateOrderForm:updateOrderOptions.markAsCollected'),
          action: onOrderCollected
        };
      case OrderStatuses.DELIVERY:
        return {
          label: t('UpdateOrderForm:updateOrderOptions.markAsDelivering'),
          action: onOrderDelivering
        };
      case OrderStatuses.DELIVERING:
        return {
          label: t('UpdateOrderForm:updateOrderOptions.markAsDelivered'),
          action: onOrderDelivered
        };
      default:
        return {
          nextStatus: '',
          action: () => {}
        };
    }
  };

  const { label: nextStatusLabel, action: nextStatusAction } = getOrderNextStatusAndAction(order);

  if (
    setOrderCancelled.isPending ||
    setOrderAccepted.isPending ||
    setOrderCleaned.isPending ||
    setOrderCleaning.isPending ||
    setOrderDelivered.isPending ||
    setOrderCollected.isPending ||
    setOrderPicking.isPending ||
    setOrderDelivering.isPending
  ) {
    return (
      <Flex justify="center" align="center" h="150px" direction="column">
        <Loader />
        <Text mt="sm">{t('ScanOrderModal:updatingOrder')}</Text>
      </Flex>
    );
  }

  return (
    <>
      {/* Aquí puedes agregar botones de acción */}
      <Group mb="md" mt="md" justify="flex-end">
        {nextStatusLabel && nextStatusAction && (
          <Button
            variant="light"
            onClick={() =>
              nextStatusAction({
                sequence: order.sequence || '',
                storeId: sourceStoreId,
                collectWithAmountDue: false, //USED ON orderCollected mutation
                params: {
                  //USED ON orderCleaned mutation
                  // TODO: Verify whether scan-to-clean should show this checkbox when outsourcing is active.
                  markOutsourcedAsReturned: true // WE CLOSE THE OUTSOURCED ORDERS RELATED TO THIS ORDER
                }
              })
            }>
            {nextStatusLabel}
          </Button>
        )}
        {canEditOrders && (
          <Button
            variant="light"
            disabled={!order._id || !sourceStoreId}
            onClick={openNotifications}
          >
            {t('OrdersCardMoreActionMenu:notificationResend.title')}
          </Button>
        )}
        <Button variant="light" onClick={onEditOrder} disabled={!canEditOrders}>
          {t('CommonButtons:edit')}
        </Button>

        <Button variant="light" onClick={() => showPaymentLinesModal(order)}>
          {t('OrdersPage:paymentLinesModal.add')}
        </Button>

        {order.status !== OrderStatuses.CANCELLED && (
          <Button
            variant="light"
            color="red"
            onClick={() =>
              ConfirmationModal({
                title: t('Notifications:CRUD.cancelConfirmation'),
                onConfirm: () =>
                  onOrderCancelled({
                    sequence: order.sequence || '',
                    storeId: order.createdIn?._id || ''
                  }),
                onCancel: () => {}
              })
            }>
            {t('CommonButtons:cancelOrder')}
          </Button>
        )}
      </Group>
      <Box mt="md" p="sm" bg="gray.1" style={{ borderRadius: 6, overflow: 'auto' }}>
        <OrderPaymentLinesModal
          onAddPaymentLine={() => {
            refetchOrder();
          }}
          title={paymentLinesModal.title}
          close={paymentLinesModal.close}
          opened={paymentLinesModal.opened}
          order={paymentLinesModal.order || order}
          onDeletePaymentLine={() => {
            refetchOrder();
          }}
          onUpdatePaymentLine={() => {
            refetchOrder();
          }}
          pinUser={paymentLinesModal.pinUser}
        />
        <Text size="sm">
          <strong>{t('FieldName:store')}:</strong> {order.createdIn?.name}
        </Text>
        <Text size="sm">
          <strong>{t('FieldName:order')}:</strong>{' '}
          {buildOrderFolio(order?.folioPrefix, order?.sequence)}
        </Text>
        <Text size="sm">
          <strong>{t('FieldName:customer')}:</strong>{' '}
          {getCustomerFullName(order.customer?.name, order.customer?.lastName) || 'N/A'}
        </Text>
        <FrequentCustomerCardSummary card={order.customer?.frequentCustomerCard} />
        <Text size="sm">
          <strong>{t('FieldName:status')}:</strong>
          {t(
            `FieldName:orderStatuses.${
              OrderStatuses[order?.status.toUpperCase() as keyof typeof OrderStatuses]
            }`
          )}
        </Text>
        <Text size="sm">
          <strong>{t('FieldName:pieces')}:</strong> {allPieces}
        </Text>
        <Text size="sm">
          <strong>{t('FieldName:total')}:</strong>
          {formatMoneyString(order.total)}
        </Text>
        <Text size="sm">
          <strong>{t('FieldName:amountDue')}:</strong>
          {formatMoneyString(amountDue)}
        </Text>
        <Table striped highlightOnHover withTableBorder withColumnBorders width={'100%'}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('OrdersPage:productsModal.product')}</Table.Th>
              <Table.Th>{t('OrdersPage:productsModal.section')}</Table.Th>
              <Table.Th>{t('OrdersPage:productsModal.colors')}</Table.Th>
              <Table.Th>{t('OrdersPage:productsModal.damage')}</Table.Th>
              <Table.Th>{t('OrdersPage:productsModal.stain')}</Table.Th>
              <Table.Th>{t('OrdersPage:productsModal.brand')}</Table.Th>
              <Table.Th>{t('OrdersPage:productsModal.notes')}</Table.Th>
              <Table.Th>{t('OrdersPage:productsModal.status')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Box>
    </>
  );
};

export function ScanOrderModal({
  opened,
  onClose,
  onScanned = () => {}
}: {
  opened: boolean;
  onClose: () => void;
  onScanned?: (orderId: string) => void;
}) {
  const { t } = useTranslation(['FieldName', 'ScanOrderModal']);
  const inputRef = useRef<HTMLInputElement>(null);
  const notificationOpen = useRef(false);
  const [notificationOpened, setNotificationOpened] = useState(false);
  const [notificationScope, setNotificationScope] = useState<{ orderId: string; storeId: string } | null>(null);
  const [searchedOrderId, setSearchedOrderId] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorType, setErrorType] = useState<'invalid' | 'notfound' | null>(null);
  const [lastScanInput, setLastScanInput] = useState('');
  const [parsedScan, setParsedScan] = useState<
    ({ orderId: string; customerId?: string } | { legacyOrderId: string }) | null
  >(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const enterDelayRef = useRef<number | null>(null);
  const posthog = usePostHog();

  const {
    data: scannedOrder,
    refetch,
    isFetching,
    isSuccess,
    isError
  } = useGetOrderById(searchedOrderId, { fetchSection: 'true' }, { enabled: false, retry: false });
  const { data: scannedStore } = useGetStoreById(
    !!scannedOrder?.createdIn?._id,
    scannedOrder?.createdIn?._id
  );

  useEffect(() => {
    if (opened) {
      setSearchedOrderId('');
      setShowError(false);
      trackEvent(posthog, MonitoringEventTypes.SCANNER_ORDER_SCANNED_PRESENTED, {});
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      inputRef.current && (inputRef.current.value = '');
    }
  }, [opened]);

  const resetScanState = () => {
    setSearchedOrderId('');
    setParsedScan(null);
    setShowError(false);
    setErrorType(null);
    setLastScanInput('');
    if (enterDelayRef.current) {
      window.clearTimeout(enterDelayRef.current);
      enterDelayRef.current = null;
    }
    inputRef.current && (inputRef.current.value = '');
  };

  useEffect(() => {
    if (!opened) return;
    resetScanState();
    trackEvent(posthog, MonitoringEventTypes.SCANNER_ORDER_SCANNED_PRESENTED, {});
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [opened]);

  const handleSearch = async () => {
    if (notificationOpen.current) return;
    const cleanInput = (inputRef?.current?.value ?? '').trim();
    if (!cleanInput) return;
    setLastScanInput(cleanInput);

    const parsed = parseScannedCode(cleanInput);
    if ('error' in parsed) {
      setShowError(true);
      setErrorType('invalid');
      setParsedScan(null);
      // inputRef.current.value = '';
      return;
    }

    setParsedScan(parsed);
    setErrorType(null);
    setShowError(false);

    if ('legacyOrderId' in parsed) {
      setSearchedOrderId(parsed.legacyOrderId);
    } else {
      setSearchedOrderId(parsed.orderId);
    }
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  };

  const onFocusInput = () => {
    if (isInputFocused) {
      inputRef.current?.blur();
    } else {
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!searchedOrderId) return;

    async function searchOrder() {
      const cleanInput = searchedOrderId.trim();
      if (cleanInput?.length) {
        const result = await refetch();
        if (result.isSuccess) {
          trackEvent(posthog, MonitoringEventTypes.SCANNER_ORDER_FOUND, {
            orderId: cleanInput
          });
          setShowError(false);
          setErrorType(null);
          setLastScanInput('');
          onScanned(cleanInput);
          if (inputRef?.current) {
            inputRef.current.value = '';
          }
        } else {
          trackEvent(posthog, MonitoringEventTypes.SCANNER_ORDER_NOT_FOUND, {
            orderId: cleanInput
          });
          setShowError(true);
          setErrorType('notfound');
        }
      }
    }
    searchOrder();
  }, [searchedOrderId]);

  const scanMode = (scannedStore as any)?.ticketScanMode || 'order';
  const parsedCustomerId =
    parsedScan && 'customerId' in parsedScan ? parsedScan.customerId : undefined;
  const shouldShowCustomerView =
    isSuccess && scannedOrder && parsedCustomerId && scanMode === 'customer';

  const handleDrawerClose = () => {
    if (notificationOpen.current) return;
    resetScanState();
    onClose();
  };

  const errorMessage =
    errorType === 'invalid'
      ? lastScanInput
        ? `No se encontró registro con el código escaneado: ${lastScanInput}`
        : t('ScanOrderModal:invalidQr')
      : errorType === 'notfound'
      ? lastScanInput
        ? `No se encontró registro con el código escaneado: ${lastScanInput}`
        : t('ScanOrderModal:orderNotFound')
      : '';

  const handleSelectOrderFromCustomer = (orderId: string) => {
    if (notificationOpen.current) return;
    setParsedScan({ orderId });
    setSearchedOrderId(orderId);
    setShowError(false);
    setErrorType(null);
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={handleDrawerClose}
        closeOnEscape={!notificationOpened}
        closeOnClickOutside={!notificationOpened}
        withCloseButton={!notificationOpened}
        position="right"
        size="xl"
        overlayProps={{ opacity: 0.35 }}
        title={
          shouldShowCustomerView
            ? t('ScanOrderModal:customerOrdersTitle')
            : t('ScanOrderModal:scanOrder')
        }>
        {isFetching ? (
          <Flex justify="center" align="center" h="150px" direction="column">
            <Loader />
            <Text mt="sm">{t('ScanOrderModal:searchingOrder')}</Text>
          </Flex>
        ) : (
          <>
            <Text mb="md">{t('ScanOrderModal:scanOrderTooltip')}</Text>
            <Flex justify="center" align="center" gap="xs">
              <TextInput
                flex={1}
                ref={inputRef}
                type="text"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (enterDelayRef.current) {
                      window.clearTimeout(enterDelayRef.current);
                    }
                    enterDelayRef.current = window.setTimeout(() => {
                      handleSearch();
                    }, 100);
                  }
                }}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                placeholder={t('ScanOrderModal:scanningOrderPlaceholder')}
                autoFocus
              />
              <Tooltip
                label={
                  isInputFocused
                    ? t('CommonButtons:listeningForScanner')
                    : t('CommonButtons:startScanning')
                }>
                <ActionIcon
                  variant="filled"
                  color={isInputFocused ? 'blue' : 'gray'}
                  onClick={onFocusInput}>
                  <IconQrcode />
                </ActionIcon>
              </Tooltip>
            </Flex>
            {showError && (
              <Text c="red" mt="xs">
                {errorMessage}
              </Text>
            )}

            {shouldShowCustomerView ? (
              <CustomerScannedModalContent
                storeId={scannedOrder?.createdIn?._id || ''}
                customerId={parsedCustomerId!}
                highlightOrderId={scannedOrder?._id}
                customerName={getCustomerFullName(
                  scannedOrder?.customer?.name,
                  scannedOrder?.customer?.lastName
                )}
                customerPhone={
                  (scannedOrder?.customer as any)?.phone ||
                  (scannedOrder?.customer as any)?.phoneNumber ||
                  (scannedOrder as any)?.customerPhone
                }
                onSelectOrder={handleSelectOrderFromCustomer}
                onClose={handleDrawerClose}
              />
            ) : (
              isSuccess &&
              scannedOrder && (
                <ScannedOrderDetails
                  order={scannedOrder}
                  refetchOrder={refetch}
                  close={handleDrawerClose}
                  openNotifications={() => {
                    const storeId = typeof scannedOrder.createdIn === 'string' ? scannedOrder.createdIn : scannedOrder.createdIn?._id || '';
                    if (!scannedOrder._id || !storeId) return;
                    setNotificationScope({ orderId: scannedOrder._id, storeId });
                    notificationOpen.current = true;
                    setNotificationOpened(true);
                  }}
                />
              )
            )}
          </>
        )}
      </Drawer>
      {notificationScope && (
        <OrderNotificationModal
          opened={notificationOpened}
          onClose={() => {
            notificationOpen.current = false;
            setNotificationOpened(false);
          }}
          orderId={notificationScope.orderId}
          storeId={notificationScope.storeId}
        />
      )}
    </>
  );
}
